"use client";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import dynamic from "next/dynamic";

import { OspToast } from "@ospweb/sdk-ui";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/config/BaseLayoutConfig";

const ReactQueryDevtools = dynamic(
  () =>
    import("@tanstack/react-query-devtools").then(
      (mod) => mod.ReactQueryDevtools,
    ),
  {
    ssr: false,
  },
);

const I18n = I18nUtil.createScoped(Translations);

const getErrorCodeMap = (): Map<number, string> => {
  return new Map<number, string>([
    [140001, I18n.t("Order time must be within today")],
    [140004, I18n.t("Operation failed. Please try again later.")],
    [146090, I18n.t("Asset not found.")],
    [146091, I18n.t("Account does not exist.")],
    [146092, I18n.t("Position asset limit exceeded.")],
    [146093, I18n.t("Order not found.")],
    [146094, I18n.t("Position asset already exists.")],
    [146095, I18n.t("Position history fix failed.")],
    [146096, I18n.t("Data is being calculated. Please try again later.")],
    [146097, I18n.t("Authorization Failed")],
    [
      146098,
      I18n.t("This specific wallet address is not bound to your account"),
    ],
    [146100, I18n.t("Operation Too Frequent")],
    [146041, I18n.t("this account bind to another")],
    [146099, I18n.t("Insufficient credit to activate new account")],
    [106033, I18n.t("Platform id has been Bound")],
    [106019, I18n.t("Operation failed, please try again")],
  ]);
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000, // 30秒内数据视为新鲜，避免不必要的重新请求
      refetchOnWindowFocus: false, // 不在窗口聚焦时自动重新获取，减少网络请求
      retry: 1, // 失败重试1次而非默认的3次，快速失败
      gcTime: 5 * 60 * 1000, // 5分钟后清理缓存，平衡内存使用和数据新鲜度
    },
    mutations: {
      onError: (error: any) => {
        const errorCode = error?.error?.code;
        if (errorCode) {
          const content = getErrorCodeMap().get(errorCode);
          if (content) {
            OspToast.error({ content });
          }
        }
      },
    },
  },
});

export const DISABLE_TURNSTILE =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1" ||
    process.env.NODE_ENV === "development");

export function BaseLayoutConfig({ children }: { children: React.ReactNode }) {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {process.env.NODE_ENV === "development" && (
        <ReactQueryDevtools initialIsOpen={false} position="bottom" />
      )}
    </QueryClientProvider>
  );
}
